// What the current scene is running on, derived from the store: the pack on screen, whether it's
// live, the timeline (the main sim's time base once one loads, else the scene's own) and its
// Methods. The timeline, the clock, the shortcuts and the Methods panel all read these, so they
// never disagree. A view on its own pack (a tissue pack) shows that pack alone: the scene's sims
// stay loaded for its other views but aren't on screen. Its timeline is the run the viewport plays
// on that pack (H01's simulated activity), if it has one, and its Methods are the pack's and the run's.

import { drawsLayer } from '../viewport/draws.ts';
import { scenarioById, type Scenario, type ScenePreset, type Timeline, type TimelineEvent } from '../scenarios/index.ts';
import type { AtlasState, EngineState, SimSlot } from './store.ts';
import { simMethods, prettyName, type SimMethods } from '../data/sims/methods.ts';
import { axisFromHeader, axisFromTimeBase, duration, frameTime, pacingOf, type TimeAxis } from '../data/sims/time.ts';
import type { CommonHeader, DynamicsHeader, LifetimeHeader, SimFormat } from '../data/sims/types.ts';
import type { Overlay } from '../data/sims/overlay.ts';
import { comparisonCaption, isPatientComparison } from '../data/sims/comparison.ts';
import { tissueMethods, type TissueMethods } from '../data/tissue.ts';
import type { LoadedRun } from '../viewport/api.ts';

export type SceneState = Pick<AtlasState, 'scenario' | 'sims'> & Partial<Pick<AtlasState, 'viewPack' | 'catalogue' | 'packId' | 'load' | 'preset' | 'journey' | 'engine'>>;

const NO_SLOTS: SimSlot[] = [];

/** The pack on screen: the view's own when it has one, else the scene's. */
/** The head an option of the scene's choices brings (ChoiceOption.pack, Stimulate's subjects), the chosen or the default one. */
const chosenPack = (s: Pick<AtlasState, 'scenario'> & Partial<Pick<AtlasState, 'choices'>>): string | null => {
  for (const ch of scenarioById(s.scenario).choices ?? []) {
    if (!ch.options?.some((o) => o.pack)) continue;
    const v = s.choices?.[ch.id] ?? ch.default;
    const pack = (ch.options.find((o) => o.value === v) ?? ch.options[0])?.pack;
    if (pack) return pack;
  }
  return null;
};

/** What `packOf` gives for a run in its own frame: this, then the run's id. The bridge loads the frame the run's header names. */
export const FRAME = 'frame:';

/** The run whose own frame a `packOf` key loads, or null for a pack. */
export const frameRunOf = (key: string | null | undefined): string | null => (key?.startsWith(FRAME) ? key.slice(FRAME.length) : null);

/**
 * The run in its own frame that the scene's chosen option plays (ChoiceOption.frame, the column), by its id: only in a view that
 * frames its cells, and on a viewport whose `ready.packs` lists `frame`. A chosen index run is its path (`cells/<id>`) or, from a
 * link, its id.
 */
const chosenFrame = (s: Pick<AtlasState, 'scenario'> & Partial<Pick<AtlasState, 'choices' | 'preset' | 'engine' | 'sims'>>): string | null => {
  const sc = scenarioById(s.scenario);
  if (!sc.presets.find((p) => p.id === s.preset)?.frameCells || !s.engine?.packs?.includes('frame')) return null;
  // Once the scene's options are read (whichever run they were read for: they list the index), a run the index doesn't list, or
  // none after an error, plays on the scene's pack: the sims fall back to another run, which the frame wouldn't hold.
  const read = s.sims?.scenario === s.scenario && (s.sims.status === 'ready' || s.sims.status === 'error') ? s.sims.options : null;
  for (const ch of sc.choices ?? []) {
    const v = s.choices?.[ch.id] ?? ch.default;
    const o = ch.options?.find((o) => o.value === v || !!ch.index?.some((dir) => v === `${dir}/${o.value}`));
    if (!o?.frame) continue;
    const listed = !read || !!(read[ch.id] as { id?: string; value: string }[] | undefined)?.some((x) => x.id === o.value || x.value === o.value);
    return listed ? o.value : null;
  }
  return null;
};

/** The pack on screen: a view's own, else the chosen run's own frame (`FRAME` and its id), else the chosen subject's, else the scene's. */
export const packOf = (s: Pick<AtlasState, 'scenario' | 'viewPack'> & Partial<Pick<AtlasState, 'choices' | 'preset' | 'engine' | 'sims'>>): string => {
  if (s.viewPack) return s.viewPack;
  const frame = chosenFrame(s);
  return frame ? `${FRAME}${frame}` : (chosenPack(s) ?? scenarioById(s.scenario).pack);
};

/** Whether the viewport can show a view: one on its own pack needs a viewport that loads connectome packs (`ready.packs`, when it
 *  says), and a journey view one that lists the journey (`ready.journeys`, when it says: `[]` from a build without them). */
export const offers = (p: ScenePreset, engine: Pick<EngineState, 'packs' | 'journeys' | 'layers'> & Partial<Pick<EngineState, 'kind'>>): boolean =>
  // A view on its own pack needs a viewport that loads that kind of pack: a connectome pack unless the view says anatomy
  // (Lifetime's Parkinson's stages on the MNI template, which the 2D preview loads too).
  (!p.pack || !engine.packs || engine.packs.includes(p.packKind ?? 'connectome')) &&
  (!p.journey || !engine.journeys || engine.journeys.includes(p.journey.id)) &&
  // A view drawn through a sim layer (3D flow's surface:wall) needs a viewport that lists it (`ready.layers`, when it says); the
  // 2D preview draws no sim surfaces.
  (!p.needsLayer || drawsSurfaceLayer(engine, p.needsLayer));

/** Whether this viewport draws a view's sim layer: not the 2D preview, and `ready.layers` lists it (unknown before `ready`: assumed). */
export const drawsSurfaceLayer = (engine: Pick<EngineState, 'layers'> & Partial<Pick<EngineState, 'kind'>>, layer: string): boolean => engine.kind !== 'mock' && drawsLayer(engine.layers, layer);

/** The scene's views this viewport can show. */
export const offeredPresets = (sc: Scenario, engine: Pick<EngineState, 'packs' | 'journeys' | 'layers'> & Partial<Pick<EngineState, 'kind'>>): ScenePreset[] => sc.presets.filter((p) => offers(p, engine));

/** The scene's sims if they've resolved for this scene, in whichever view. */
const sceneSlots = (s: SceneState): SimSlot[] => (s.sims.scenario === s.scenario ? s.sims.slots : NO_SLOTS);
const sceneMain = (s: SceneState): SimSlot | null => sceneSlots(s).find((x) => x.slot === 'main') ?? null;

/** The scene's sims on screen (a stable array, for selectors): none in a view on its own pack. */
export function slotsOf(s: SceneState): SimSlot[] {
  return s.viewPack ? NO_SLOTS : sceneSlots(s);
}

/**
 * The slots whose run has ended by the scene's time: a secondary run on its own clock that stops
 * before the scene does (Stroke's 30 minutes of spreading depolarization in its 6 hours). Past its
 * last frame it goes dark, rather than holding that frame for the rest of the scene.
 */
export function endedSlots(s: SceneState & { time?: number }): string[] {
  const out: string[] = [];
  for (const x of slotsOf(s)) {
    if (x.slot === 'main' || x.appOnly) continue;
    let a: TimeAxis;
    try {
      a = headerAxis(x.format, x.header);
    } catch {
      continue;
    }
    if (!a.periodic && a.frames > 1 && (s.time ?? 0) > frameTime(a, a.frames - 1)) out.push(x.slot);
  }
  return out;
}

export function mainSlot(s: SceneState): SimSlot | null {
  return slotsOf(s).find((x) => x.slot === 'main') ?? null;
}

/** Live: the scene needs no sim, or its main sim is loaded and is a real model output. A view on its own pack shows that pack's data alone. */
export function isLive(s: SceneState): boolean {
  if (s.viewPack) return true;
  const sc = scenarioById(s.scenario);
  if (!sc.sims?.length) return sc.status === 'live';
  const main = mainSlot(s);
  return !!main && !main.standIn;
}

let lastDrawn: { overlays: Overlay[]; catalogue: AtlasState['catalogue']; out: Overlay[] } | null = null;

/**
 * Overlays with at least one key in the loaded pack. One keyed only by another atlas's ids (pharma's
 * Schaefer parcels, until a crosswalk lands) would colour nothing, so it's neither sent nor keyed.
 */
export function drawnOverlays(s: Pick<AtlasState, 'overlays' | 'catalogue'>): Overlay[] {
  if (lastDrawn && lastDrawn.overlays === s.overlays && lastDrawn.catalogue === s.catalogue) return lastDrawn.out;
  const byId = s.catalogue?.byId;
  const out = byId ? s.overlays.filter((o) => o.keys.some((k) => byId.has(k))) : s.overlays;
  lastDrawn = { overlays: s.overlays, catalogue: s.catalogue, out };
  return out;
}

/** Live mode: the solver has loaded and the network differs from the run's (a clot, the collaterals off)
 *  or the clot tool is armed. The picture and the flow trace then follow the solver. */
export const liveActive = (s: Pick<AtlasState, 'live' | 'stroke'>): boolean =>
  s.live.status === 'ready' && (s.live.tool || s.stroke.occlusion !== null || !s.stroke.collaterals);

/** The Dose slider's factor on the scene's pharma course: its mass over the course's own, where the
 *  course follows the model it rescales by (data/sims/dose.ts); 1 otherwise. */
export const doseFactor = (s: Pick<AtlasState, 'drug' | 'drugDose'>): number =>
  s.drug?.fits && s.drugDose !== null && s.drug.dose > 0 ? s.drugDose / s.drug.dose : 1;

/** A header's playback time base (dynamics: its visual base). */
export function headerAxis(format: SimFormat, h: CommonHeader): TimeAxis {
  if (format === 'dynamics') {
    const d = h as DynamicsHeader;
    const tb = d.time_bases?.[d.time?.timebase ?? 'visual'];
    if (tb) return axisFromTimeBase(tb);
  }
  if (format === 'lifetime' || format === 'sections') {
    // lifetime-sim-v0 (and its sections sibling) lists every frame's time in `time.values` (s since the first frame) and is never periodic.
    const t = (h as LifetimeHeader).time;
    if (t?.values?.length) return axisFromHeader({ ...t, times_s: t.values, periodic: false });
  }
  const a = axisFromHeader(h.time);
  return format === 'cells' ? { ...a, periodic: false } : a;
}

const WEEK_S = 604800;

/** A lifetime sequence's own clock (lifetime-sim-v0): read in its age unit where that's weeks, from its first frame's age (the
 *  fetal atlas's 21 gestational weeks, the neonatal atlas's 37 weeks postmenstrual age), at a quarter, a half, one and two weeks a
 *  second (the fetal fifteen weeks in 60, 30, 15 or 7.5 s; the neonatal seven, whose three frames are 3 and 4 weeks apart, in 28 to
 *  3.5 s); a sequence in another unit at a quarter to two frames a second. Null for a sequence of one frame. */
function lifetimeClock(h: LifetimeHeader, axis: TimeAxis): { unit: Timeline['unit']; origin: number; speeds: number[] } | null {
  const last = frameTime(axis, axis.frames - 1);
  const step = axis.frames > 1 ? (last - axis.t0) / (axis.frames - 1) : 0;
  if (!(step > 0)) return null;
  const weeks = h.age?.units === 'week' || h.age?.units === 'weeks';
  const age0 = h.age?.values?.[0] ?? h.age?.at_time_zero ?? 0;
  return { unit: weeks ? 'week' : unitFor(last), origin: weeks ? age0 - axis.t0 / WEEK_S : 0, speeds: weeks ? [WEEK_S / 4, WEEK_S / 2, WEEK_S, 2 * WEEK_S] : [step / 4, step / 2, step, 2 * step] };
}

export function unitFor(seconds: number): Timeline['unit'] {
  if (seconds < 2) return 'ms';
  if (seconds < 180) return 's';
  if (seconds < 3 * 3600) return 'min';
  return 'h';
}

/** Playback rates for a run: real time for a heartbeat or a seizure, minutes or hours per second for stroke. */
export function speedsFor(seconds: number, periodic: boolean, playbackRate?: number): number[] {
  if (playbackRate && playbackRate > 0) return [playbackRate / 4, playbackRate / 2, playbackRate, playbackRate * 2];
  if (periodic) return [0.1, 0.25, 0.5, 1];
  if (seconds <= 2) return [0.01, 0.05, 0.1, 0.25];
  if (seconds <= 300) return [0.25, 0.5, 1, 2, 4];
  if (seconds <= 3 * 3600) return [10, 60, 300, 600];
  return [600, 1800, 3600, 7200];
}

/** Real time where it's offered; else the rate that plays the run in about 20 s. */
export function defaultSpeed(tl: Timeline, playbackRate?: number): number {
  if (playbackRate && tl.speeds.includes(playbackRate)) return playbackRate;
  if (tl.speeds.includes(1)) return 1;
  const want = (tl.duration - (tl.start ?? 0)) / 20;
  return tl.speeds.reduce((best, v) => (Math.abs(Math.log(v / want)) < Math.abs(Math.log(best / want)) ? v : best), tl.speeds[0] ?? 1);
}

const EVENT_LABEL: Record<string, string> = { 'current-clamp': 'Current step', 'synaptic-cluster': 'Synaptic input', vesicular_release: 'Release' };

/** Timeline markers from the header: `scenario.events` (hemo, pharma, dynamics) and cells' `scenario.stimuli`. */
export function headerEvents(h: CommonHeader, end: number): TimelineEvent[] {
  const out: TimelineEvent[] = [];
  const add = (t: unknown, type: string, label?: unknown) => {
    if (typeof t !== 'number' || !Number.isFinite(t) || t < 0 || t > end + 1e-9) return;
    const name = typeof label === 'string' && label ? label : (EVENT_LABEL[type] ?? prettyName(type).replace(/^./, (c) => c.toUpperCase()));
    if (!out.some((e) => e.t === t && e.label === name)) out.push({ t, label: name });
  };
  for (const e of h.scenario?.events ?? []) add(e.t_s ?? e.time_s ?? e.t, e.type ?? e.kind ?? 'event', e.label);
  for (const e of h.scenario?.stimuli ?? []) add(e.t_s ?? e.t_on_s ?? e.t, e.type ?? e.kind ?? 'stimulus', e.label);
  // A run fitted to a patient's seizure marks the model's onset, where it lines up with the clinical one.
  add(h.onset_s, 'onset', 'Onset');
  return out.sort((a, b) => a.t - b.t);
}

/** Memoized on the scene and its sims, so React selectors get a stable object. */
function memo<T>(compute: (s: SceneState) => T): (s: SceneState) => T {
  let last: { scenario: string; sims: SceneState['sims']; out: T } | null = null;
  return (s) => {
    if (last && last.scenario === s.scenario && last.sims === s.sims) return last.out;
    const out = compute(s);
    last = { scenario: s.scenario, sims: s.sims, out };
    return out;
  };
}

/** The main sim's timeline, whichever view is on screen: null for a snapshot, the scene's own before any sim loads. */
export const sceneTimelineOf = memo(computeTimeline);

let lastViewRun: { key: string; out: Timeline } | null = null;

/** The run the viewport plays in a view on its own pack, as its `load` reply says; null before then, or for a pack with none. */
export function viewRunOf(s: SceneState): LoadedRun | null {
  const l = s.load;
  if (!s.viewPack || s.packId !== s.viewPack || l?.status !== 'ready') return null;
  const run = l.runs?.find((r) => r.id === l.run);
  return run?.loaded && run.durationS > 0 ? run : null;
}

/** A view on its own pack: the timeline of the run the viewport plays there (memoized on the run). */
export function viewTimelineOf(s: SceneState): Timeline | null {
  const run = viewRunOf(s);
  if (!run) return null;
  const key = `${s.viewPack} ${run.id} ${run.durationS}`;
  if (lastViewRun?.key !== key) {
    const d = run.durationS;
    lastViewRun = { key, out: { duration: d, unit: unitFor(d), periodic: false, events: [], speeds: speedsFor(d, false) } };
  }
  return lastViewRun.out;
}

/** A view that draws a still comparison itself (ScenePreset.still, Drugs' measured ΔFC): no clock, and the scene's runs aren't sent. */
export const stillView = (s: Pick<AtlasState, 'scenario'> & Partial<Pick<AtlasState, 'preset'>>): boolean => !!scenarioById(s.scenario).presets.find((p) => p.id === s.preset)?.still;

/** Whether the scene is in a view on its own anatomy pack (Lifetime's Parkinson's stages on the MNI template): that pack's
 *  systems show as the view's layers say, not as the scene leaves them out of its own pack. A connectome-pack view is unchanged. */
export const viewOnAnatomy = (s: Pick<AtlasState, 'scenario'> & Partial<Pick<AtlasState, 'viewPack'>>): boolean =>
  !!s.viewPack && scenarioById(s.scenario).presets.find((p) => p.pack === s.viewPack)?.packKind === 'anatomy';

/** A view that draws its own run through the viewport while the scene's stand aside (ScenePreset.aside, Blood flow's 3D flow). */
export const asideView = (s: Pick<AtlasState, 'scenario'> & Partial<Pick<AtlasState, 'preset'>>): boolean => !!scenarioById(s.scenario).presets.find((p) => p.id === s.preset)?.aside;

/** Whether the scene's runs stand aside: a still view or an aside one. The bridge sends none of them, Methods lists the view's
 *  own, and the sections that drive them (Vessels, Particles) stand aside too. */
export const runsAside = (s: Pick<AtlasState, 'scenario'> & Partial<Pick<AtlasState, 'preset'>>): boolean => stillView(s) || asideView(s);

/** The journey a view plays (ScenePreset.journey, Explore's Powers of Ten), or null. */
export const journeyOf = (s: Pick<AtlasState, 'scenario'> & Partial<Pick<AtlasState, 'preset'>>): NonNullable<ScenePreset['journey']> | null =>
  scenarioById(s.scenario).presets.find((p) => p.id === s.preset)?.journey ?? null;

/** A journey plays at real time, half, a quarter, or double. */
export const JOURNEY_SPEEDS = [0.25, 0.5, 1, 2];

let lastJourneyTl: { key: string; out: Timeline } | null = null;

/** A journey view's timeline: the viewport's reply once it has started (its stages as the transport's events, the short
 *  dissolves ranked below the stages they join), else the preset's length alone, so the transport stands before the reply. */
export function journeyTimelineOf(s: SceneState): Timeline | null {
  const j = journeyOf(s);
  // A viewport that has said it doesn't play it (`ready.journeys` without it): no clock either; the HUD says why.
  if (!j || (s.engine?.journeys && !s.engine.journeys.includes(j.id))) return null;
  const reply = s.journey?.id === j.id ? s.journey : null;
  const key = reply ? `${j.id} ${reply.durationS} ${reply.stages.map((st) => `${st.id}@${st.t0}`).join(',')}` : `${j.id} ${j.duration}`;
  if (lastJourneyTl?.key !== key) {
    const duration = reply?.durationS ?? j.duration;
    const events: TimelineEvent[] = (reply?.stages ?? []).map((st) => ({ t: st.t0, label: st.name, ...(st.t1 - st.t0 < 1 ? { rank: 1 } : {}) }));
    lastJourneyTl = { key, out: { duration, unit: unitFor(duration), periodic: false, events, speeds: JOURNEY_SPEEDS } };
  }
  return lastJourneyTl.out;
}

/** The timeline on screen: the scene's, or in a view on its own pack, its run's (none until the pack's load says it has one), or
 *  a journey's; none in a still view. */
export const timelineOf = (s: SceneState): Timeline | null => (stillView(s) ? null : journeyOf(s) ? journeyTimelineOf(s) : s.viewPack ? viewTimelineOf(s) : sceneTimelineOf(s));

function computeTimeline(s: SceneState): Timeline | null {
  const sc = scenarioById(s.scenario);
  const main = sceneMain(s);
  if (!main) return sc.timeline ?? null;
  const axis = headerAxis(main.format, main.header);
  if (axis.frames <= 1 || main.header.time?.snapshot) return null;
  const end = axis.periodic ? duration(axis) : frameTime(axis, axis.frames - 1);
  if (!(end > 0)) return null;
  const rate = main.header.time?.playback_rate;
  const pacing = pacingOf(main.header.time);
  // A run recorded from after 0 (Yao's circuits keep 2.0–4.5 s, past their settling) plays from its first frame.
  const start = !axis.periodic && axis.t0 > 0 && axis.t0 < end ? axis.t0 : 0;
  // A lifetime sequence keeps its own clock: gestational weeks for the fetal atlas, a frame a second by default.
  const life = main.format === 'lifetime' ? lifetimeClock(main.header as LifetimeHeader, axis) : null;
  return {
    duration: end,
    ...(start ? { start } : {}),
    unit: life?.unit ?? unitFor(end),
    ...(life ? { origin: life.origin } : {}),
    periodic: axis.periodic,
    events: main.events ? main.events.filter((e) => e.t >= start && e.t <= end + 1e-9) : headerEvents(main.header, end),
    speeds: life ? life.speeds : withShortRuns(speedsFor(end - start, axis.periodic, rate), sceneSlots(s), end),
    ...(pacing && !axis.periodic ? { pacing } : {}),
  };
}

/** Rates a run shorter than the scene can be watched at: one that plays it in about 30 s. */
const SHORT_RUN_SPEEDS = [1, 10, 60, 300, 600];

/** The main run's speeds, and one for each secondary run that ends well before it (Stroke's 30 minutes
 *  of spreading depolarization, which its slowest 10 min/s would play in 3 s). */
function withShortRuns(speeds: number[], slots: SimSlot[], end: number): number[] {
  const out = new Set(speeds);
  for (const x of slots) {
    if (x.slot === 'main' || x.appOnly) continue;
    let a: TimeAxis;
    try {
      a = headerAxis(x.format, x.header);
    } catch {
      continue;
    }
    const d = a.periodic ? 0 : duration(a);
    if (!(d > 0) || d > end / 4 || d / 30 >= Math.min(...speeds)) continue;
    out.add(SHORT_RUN_SPEEDS.reduce((best, v) => (Math.abs(Math.log(v / (d / 30))) < Math.abs(Math.log(best / (d / 30))) ? v : best)));
  }
  return [...out].sort((p, q) => p - q);
}

export interface SceneMethods {
  caption: string;
  text: string;
  /** Every sim on screen (main first), as its header describes it. */
  sims: (SimMethods & { slot: string; label?: string })[];
  /** A view on a tissue pack: the pack's source, licence and how its drawn layers were made. */
  tissue?: TissueMethods;
}

const sceneMethodsOf = memo(computeMethods);
let lastView: { pack: string; catalogue: AtlasState['catalogue'] | undefined; run: LoadedRun | null; out: SceneMethods } | null = null;

/** Methods for what's on screen: the scene's sims, or in a view on its own pack, that pack and the run the
 *  viewport plays on it (its hint until its catalogue loads). */
export function methodsOf(s: SceneState): SceneMethods {
  if (!s.viewPack) return sceneMethodsOf(s);
  const playing = viewRunOf(s);
  if (lastView && lastView.pack === s.viewPack && lastView.catalogue === s.catalogue && lastView.run === playing) return lastView.out;
  const c = s.catalogue;
  const sc = scenarioById(s.scenario);
  const view = sc.presets.find((p) => p.pack === s.viewPack);
  let out: SceneMethods;
  if (c?.kind === 'connectome' && c.pack.id === s.viewPack) {
    // The run.json the catalogue read is the default run's: it describes the run on screen only if that's the one playing.
    const info = playing && c.activity?.run?.id === playing.id ? c.activity.run : null;
    const note = playing ? sc.notes?.[playing.id] : undefined;
    const tissue = tissueMethods(c.pack, playing ? { id: playing.id, title: playing.title, info, ...(note ? { note } : {}) } : null);
    out = { caption: tissue.caption, text: tissue.text, sims: [], tissue };
  } else out = { caption: view?.hint ?? s.viewPack, text: view?.hint ?? '', sims: [] };
  lastView = { pack: s.viewPack, catalogue: c, run: playing, out };
  return out;
}

function computeMethods(s: SceneState): SceneMethods {
  const sc = scenarioById(s.scenario);
  // The scene's note on a run follows the run's own text: the main run's in the sheet's text, another's in its section.
  const noted = (text: string, id: string) => {
    const note = sc.notes?.[id];
    return note ? (text ? `${text.replace(/[\s.]*$/, '.')} ${note}` : note) : text;
  };
  // A run's index annotation, bound to this header, is its Methods text as the producing lane wrote it.
  const sims = sceneSlots(s).map((x) => {
    const m = simMethods(x.header, x.path, x.standIn);
    const text = x.annotation?.methods ?? m.text;
    return { ...m, text: x.slot === 'main' ? text : noted(text, x.header.id), slot: x.slot, ...(x.label ? { label: x.label } : {}) };
  });
  const main = sims.find((x) => x.slot === 'main');
  if (!main) return { caption: sc.caption, text: sc.methods, sims };
  const h = sceneMain(s)!.header;
  // A real patient beside the model fitted to them: the line names the model, the patient and the data.
  const caption = isPatientComparison(h) ? comparisonCaption(h as DynamicsHeader) : main.caption;
  return { caption, text: noted(main.text || sc.methods, h.id), sims };
}
